import React, { useEffect, useState, useRef } from "react";

import { useParams } from "next/navigation";

import { QuestionCircleOutlined } from "@ant-design/icons";
import { useLazyQuery } from "@apollo/client";
import { Row, Col, Card, Popover } from "antd";
import moment from "moment";

import { GET_STOCK_HISTORY } from "@/graphql/query/stocks";
import { PROJECTION_LINE } from "@/lib/constants";
import { CurrencySymbol } from "@/lib/currencySymbol";
import { TOOLTIPS } from "@/lib/tooltip";

import ProjectionChartD3 from "../Chart/page";
import Spinner from "../spinner";

const Projection = ({ stockDetails, projection }) => {
  // State declarations
  const cardRef1 = useRef();
  const { name, exchange } = useParams();
  const [loader, setLoader] = useState(false);
  const [select, setSelect] = useState("");
  const [stockHistory, setStockHistory] = useState([
    {
      stockHistory: {},
      stockPrice: {},
    },
  ]);
  const [highLowPrice, setHighLowPrice] = useState({
    lowPrice: "",
    highPrice: "",
  });
  const [currencySymbol, setCurrencySymbol] = useState("");
  const [stockData, setStockData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [projectionData, setProjectionData] = useState([]);
  const [projectionAllData, setProjectionAllData] = useState({});

  // Query

  const [getStockHistory, { loading: stockHistoryLoading }] =
    useLazyQuery(GET_STOCK_HISTORY);

  const handleProjectionData = (data, period) => {
    if (!data) return;
    const { date, share_price } = data;
    const lastDate = moment(date).format("YYYY-MM-DD");
    let key = "";
    let futureDate = 1;
    if (period === "1Y") {
      key = "oneYearFairValue";
    } else if (period === "5Y") {
      key = "fiveYearFairValue";
      futureDate = 5;
    } else if (period === "10Y") {
      key = "tenYearFairValue";
      futureDate = 10;
    }
    const oneYearAfter = moment(date)
      .add(futureDate, "years")
      .format("YYYY-MM-DD");

    // Generate data for each category
    const generateProjectionData = (key, name) => {
      const targetPrice = data[name]?.[key];
      const sharePrice = share_price;
      return [
        { x: new Date(lastDate), y: parseFloat(sharePrice?.toFixed(2)) },
        { x: new Date(oneYearAfter), y: parseFloat(targetPrice?.toFixed(2)) },
      ];
    };
    const tykr = generateProjectionData(key, "tykr");
    const ruleOne = generateProjectionData(key, "ruleOne");
    const benGraham = generateProjectionData(key, "benGraham");
    const discountedCashFlow = generateProjectionData(
      key,
      "discountedCashFlow",
    );
    const newData = [tykr, ruleOne, benGraham, discountedCashFlow];
    setProjectionData(newData);
  };

  useEffect(() => {
    if (stockDetails) {
      setProjectionAllData(projection);
      handleProjectionData(projection, "1Y");
    }
  }, [stockDetails, projection]);

  useEffect(() => {
    if (!stockHistoryLoading) {
      setLoader(stockHistoryLoading);
    }
  }, [stockHistoryLoading]);

  // Get stock history data
  const chartHandler = async (val) => {
    setLoader(true);
    const Data = await getStockHistory({
      variables: {
        stockId: name.toUpperCase(),
        exchange: exchange.toUpperCase(),
        period: val,
      },
      context: {
        headers: {
          "x-visitor-token": localStorage.getItem("id"),
        },
      },
    });
    setLoader(false);
    setStockHistory(Data?.data?.getPublicStockHistory);
    if (Object.keys(projectionAllData)?.length) {
      handleProjectionData(projectionAllData, val);
    }
  };

  // Get stock data from stock id
  useEffect(() => {
    if (name) {
      chartHandler("1Y");
      setTimeout(() => {
        setSelect("1Y");
      }, 1000);
    }
  }, [name, exchange]);

  useEffect(() => {
    if (stockDetails) {
      (async () => {
        const { currency } = stockDetails ?? {};
        setStockData(stockDetails);
        const symbol = await CurrencySymbol(currency);
        setCurrencySymbol(symbol);
      })();
    }
  }, [highLowPrice, stockDetails]);

  useEffect(() => {
    (() => {
      const symbol = CurrencySymbol(stockData?.currency);
      setCurrencySymbol(symbol);
      const Temp = stockHistory?.stockPrice?.stockPrice || [];

      const allClose = Temp?.map((d) => {
        return d?.close;
      });
      const lowPrice = Math.min(...allClose);
      const highPrice = Math.max(...allClose);

      setHighLowPrice({
        lowPrice: parseFloat(lowPrice?.toFixed(2)),
        highPrice: parseFloat(highPrice?.toFixed(2)),
      });

      //**----------------------------------- Share Price Data ---------------------------------------- */
      const newChartData = Temp?.map((d) => {
        return {
          x: new Date(d?.date),
          y: parseFloat(d?.close?.toFixed(2)),
        };
      });
      setChartData(newChartData);
    })();
  }, [stockHistory, currencySymbol, select]);

  const timePeriod = () => {
    return (
      <ul className="chart_select-month">
        {["1Y", "5Y", "10Y"]?.map((data) => {
          return (
            <li
              key={data}
              className={`month-select ${select === data && "chart-select-value"}`}
              onClick={() => {
                chartHandler(data);
                setSelect(data);
              }}
            >
              {data}
            </li>
          );
        })}
      </ul>
    );
  };
  return (
    <>
      <Row gutter={24} className={`_calcTab-row mt-24`}>
        <Col
          xs={{ span: 24 }}
          lg={{ span: 24 }}
          style={{ transition: "all 0.5s" }}
        >
          <Card ref={cardRef1} className="_chartCard h-100">
            <div>
              <div
                className={`common_chart-header d-flex justify-content-between`}
              >
                <div className="common-chart-card_header pl-0 d-flex">
                  <div className="header-title">
                    Projections Chart
                    <Popover
                      content={
                        <div>
                          <p>{TOOLTIPS["ProjectionsChart"]}</p>
                        </div>
                      }
                      title="Projections Chart"
                    >
                      <QuestionCircleOutlined className="cursor-pointer" />
                    </Popover>
                  </div>
                </div>
                {timePeriod()}
              </div>
              <div className="p-24 pr-0">
                <div className="projection-chart-header">
                  <div className="projection-title">
                    {select === "10Y" ? "10" : select === "5Y" ? "5" : "1"} Year
                    Projections
                  </div>
                  <Row
                    gutter={16}
                    className="projection-info mobile-projection-lager p-0"
                  >
                    {PROJECTION_LINE.map((projection, index) => (
                      <Col
                        key={projection?.name}
                        className="d-flex projection-lines"
                        style={{
                          marginRight: index === 3 ? 0 : 16,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <div
                          className="projection-icon"
                          style={{ backgroundColor: projection?.color }}
                        ></div>
                        <div>{projection?.name}</div>
                        <Popover
                          content={<p>{projection?.tooltip}</p>}
                          title={projection?.name}
                        >
                          <QuestionCircleOutlined className="cursor-pointer que-tooltip" />
                        </Popover>
                      </Col>
                    ))}
                  </Row>
                </div>
                {loader && <Spinner />}
                {/* Projection Chart */}
                {select &&
                  cardRef1?.current?.clientWidth &&
                  projectionData?.length > 0 &&
                  chartData?.length > 0 && (
                    <ProjectionChartD3
                      data={chartData}
                      projectionData={projectionData}
                      currencySymbol={currencySymbol}
                      width={cardRef1?.current?.clientWidth}
                      height={350}
                      time={select}
                    />
                  )}
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default Projection;
